import { FC, SVGProps } from "react";
import NextSvg from "@/asset/stack/next.svg?react";
import NestSvg from "@/asset/stack/nestjs.svg?react";
import JsSvg from "@/asset/stack/javascript.svg?react";
import TsSvg from "@/asset/stack/typescript.svg?react";
import ScssSvg from "@/asset/stack/sass.svg?react";
import GitSvg from "@/asset/stack/git.svg?react";
import PhpSvg from "@/asset/stack/php.svg?react";
import ReactSvg from "@/asset/stack/react.svg?react";
import TailwindSvg from "@/asset/stack/tailwindcss.svg?react";
import CssinjsSvg from "@/asset/stack/styledcomponents.svg?react";
import FigmaSvg from "@/asset/stack/figma.svg?react";
import ZodSvg from "@/asset/stack/zod.svg?react";
import RnSvg from "@/asset/stack/reactquery.svg?react";
import tsOrmSvg from "@/asset/stack/typeorm.svg?react";
import DrizzleSvg from "@/asset/stack/drizzle.svg?react";
import SupabaseSvg from "@/asset/stack/supabase.svg?react";
import ReactHookFormSvg from "@/asset/stack/reacthookform.svg?react";
import jquerySvg from "@/asset/stack/jquery.svg?react";
import PostgreSqlSvg from "@/asset/stack/postgresql.svg?react";
import AxiosSvg from "@/asset/stack/axios.svg?react";
import PhotoshopSvg from "@/asset/stack/photostop.svg?react";
import AwsSvg from "@/asset/stack/aws.svg?react";
import VercelSvg from "@/asset/stack/vercel.svg?react";
import DockerSvg from "@/asset/stack/docker.svg?react";
import VitestSvg from "@/asset/stack/vitest.svg?react";
import ZustandSvg from "@/asset/stack/zustand.svg?react";
import GsapSvg from "@/asset/stack/gsap.svg?react";
import TiptapSvg from "@/asset/stack/tiptap.svg?react";
import BootstrapSvg from "@/asset/stack/bootstrap.svg?react";
import NextAuthSvg from "@/asset/stack/nextauth.svg?react";
import NodeSvg from "@/asset/stack/node.svg?react";

import { cn } from "@/lib/utils";

type SVGComponent = FC<SVGProps<SVGSVGElement>>;

export type StackName =
  | "next.js"
  | "nest.js"
  | "javascript"
  | "typescript"
  | "scss"
  | "git"
  | "php"
  | "react"
  | "tailwind"
  | "jquery"
  | "styled-component"
  | "figma"
  | "zod"
  | "react-query"
  | "typeorm"
  | "drizzleorm"
  | "supabase"
  | "react-hook-form"
  | "postgresql"
  | "axios"
  | "photoshop"
  | "aws"
  | "vercel"
  | "docker"
  | "vitest"
  | "zustand"
  | "gsap"
  | "tiptap"
  | "bootstrap"
  | "nextauth"
  | "node.js";

// 아이콘과 색상을 함께 정의
const STACK_CONFIG: Record<StackName, { icon: SVGComponent; color?: string }> =
  {
    "next.js": { icon: NextSvg, color: "fill-gray-800 dark:fill-white" },
    "nest.js": { icon: NestSvg, color: "fill-[#E0234E]" },
    javascript: { icon: JsSvg, color: "fill-[#F7DF1E] rounded-xs" },
    typescript: { icon: TsSvg, color: "fill-[#3178C6] bg-white rounded-xs" },
    scss: { icon: ScssSvg, color: "fill-[#CC6699] bg-white rounded-full!" },
    git: { icon: GitSvg, color: "fill-[#F05032]" },
    php: { icon: PhpSvg, color: "fill-[#777BB4]" },
    react: { icon: ReactSvg, color: "fill-[#61DAFB]" },
    tailwind: { icon: TailwindSvg, color: "fill-[#06B6D4]" },
    jquery: { icon: jquerySvg, color: "fill-[#0769AD]" },
    "styled-component": { icon: CssinjsSvg, color: "fill-[#DB7093]" },
    figma: { icon: FigmaSvg, color: "fill-[#F24E1E]" },
    zod: { icon: ZodSvg, color: "fill-[#3068B7]" },
    "react-query": { icon: RnSvg, color: "fill-[#FF4154]" },
    typeorm: { icon: tsOrmSvg, color: "fill-[#FE0902]" },
    drizzleorm: { icon: DrizzleSvg, color: "fill-[#C5F74F]" },
    supabase: { icon: SupabaseSvg, color: "fill-[#3ECF8E]" },
    "react-hook-form": { icon: ReactHookFormSvg, color: "fill-[#EC5990]" },
    postgresql: { icon: PostgreSqlSvg, color: "fill-[#4169E1]" },
    axios: { icon: AxiosSvg, color: "fill-[#5A29E4]" },
    photoshop: { icon: PhotoshopSvg },
    aws: { icon: AwsSvg, color: "fill-[#fff]" },
    vercel: { icon: VercelSvg, color: "fill-[#fff]" },
    docker: { icon: DockerSvg },
    vitest: { icon: VitestSvg },
    zustand: { icon: ZustandSvg },
    gsap: { icon: GsapSvg },
    tiptap: { icon: TiptapSvg },
    bootstrap: { icon: BootstrapSvg },
    nextauth: { icon: NextAuthSvg },
    "node.js": { icon: NodeSvg, color: "fill-[#83cd29]" },
  };

// 별칭 매핑을 간단하게
const STACK_ALIASES: Record<string, StackName> = {
  "styled-components": "styled-component",
  styledcomponents: "styled-component",
  "tanstack@query": "react-query",
  reactquery: "react-query",
  reacthookform: "react-hook-form",
};

interface StackIconMapperProps {
  stackName: string;
  className?: string;
}

export default function StackIconMapper({
  stackName,
  className,
}: StackIconMapperProps) {
  const normalized = stackName.toLowerCase().replace(/\s+/g, "");

  // 별칭 먼저 확인
  const mappedName = (STACK_ALIASES[normalized] || normalized) as StackName;

  // 설정 가져오기 (없으면 기본값 react)
  const config = STACK_CONFIG[mappedName] || STACK_CONFIG.react;
  const IconComponent = config.icon;

  return <IconComponent className={cn("size-3", config.color, className)} />;
}
